import React from 'react';
import { Wrench } from 'lucide-react';
import { useTranslation } from '@/hooks/useTranslation';
import type { Process } from '../type/Process';
import { CollapsibleFormSection } from './CollapsibleFormSection';
import { SimpleInput } from '@/components/form/inputs/SimpleInput';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { lubricationTypes } from '../data/lubricationOptions';
import { clampingTypes } from '../data/clampingOptions';
import { toolHolderTypes } from '../data/toolHolderOptions';
import { MandatoryFieldLabel } from './MandatoryFieldLabel';

type Props = {
  formProcess: Process;
  setFormProcess: React.Dispatch<React.SetStateAction<Process>>;
  mandatoryFields: string[];
  onToggleMandatory: (key: string) => void;
};

const NONE = '__none__';
const toSelectValue = (v: string | null) => v ?? NONE;
const fromSelectValue = (v: string) => (v === NONE ? null : v);

export const ProcessFormToolingSection: React.FC<Props> = ({
  formProcess,
  setFormProcess,
  mandatoryFields,
  onToggleMandatory,
}) => {
  const { t } = useTranslation();

  const fields = [
    'lubType',
    'lubBrand',
    'holderBrand',
    'holderType',
    'clampingType',
    'clampingBrand',
  ] as const;
  const setCount = fields.filter((f) => formProcess[f] !== null).length;
  const defaultOpen = setCount > 0;

  const countLabel = t('traceability.processManager.form.sections.countLabel', {
    set: setCount,
    total: fields.length,
  });

  const renderLabelRow = (fieldKey: string, labelText: string, inputId: string) => (
    <MandatoryFieldLabel
      fieldKey={fieldKey}
      label={labelText}
      htmlFor={inputId}
      mandatoryFields={mandatoryFields}
      onToggleMandatory={onToggleMandatory}
    />
  );

  return (
    <CollapsibleFormSection
      icon={<Wrench className="h-3 w-3" aria-hidden />}
      title={t('traceability.processManager.form.sections.tooling.title')}
      toggleLabel={t('traceability.processManager.form.sections.tooling.toggleLabel')}
      countLabel={countLabel}
      defaultOpen={defaultOpen}
    >
      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
        {/* lubType — Select with checkbox */}
        <div className="flex flex-col gap-1.5">
          {renderLabelRow(
            'lubType',
            t('traceability.processManager.form.prefillFields.lubType.label'),
            'process-lubType'
          )}
          <Select
            value={toSelectValue(formProcess.lubType)}
            onValueChange={(val) =>
              setFormProcess((p) => ({ ...p, lubType: fromSelectValue(val) }))
            }
          >
            <SelectTrigger id="process-lubType" className="w-full">
              <SelectValue
                placeholder={t('traceability.processManager.form.notSet')}
              />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value={NONE}>
                {t('traceability.processManager.form.notSet')}
              </SelectItem>
              {lubricationTypes.map((opt) => (
                <SelectItem key={opt.value} value={opt.value}>
                  {t(opt.labelKey)}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>

        {/* lubBrand — SimpleInput with checkbox */}
        <div>
          {renderLabelRow(
            'lubBrand',
            t('traceability.processManager.form.prefillFields.lubBrand.label'),
            'process-lubBrand'
          )}
          <SimpleInput
            name="process-lubBrand"
            type="text"
            value={formProcess.lubBrand ?? ''}
            onChange={(val) =>
              setFormProcess((p) => ({ ...p, lubBrand: (val as string) || null }))
            }
            placeholder={t('traceability.processManager.form.prefillFields.lubBrand.placeholder')}
          />
        </div>

        {/* holderBrand — SimpleInput with checkbox */}
        <div>
          {renderLabelRow(
            'holderBrand',
            t('traceability.processManager.form.prefillFields.holderBrand.label'),
            'process-holderBrand'
          )}
          <SimpleInput
            name="process-holderBrand"
            type="text"
            value={formProcess.holderBrand ?? ''}
            onChange={(val) =>
              setFormProcess((p) => ({ ...p, holderBrand: (val as string) || null }))
            }
            placeholder={t(
              'traceability.processManager.form.prefillFields.holderBrand.placeholder'
            )}
          />
        </div>

        {/* holderType — Select with checkbox */}
        <div className="flex flex-col gap-1.5">
          {renderLabelRow(
            'holderType',
            t('traceability.processManager.form.prefillFields.holderType.label'),
            'process-holderType'
          )}
          <Select
            value={toSelectValue(formProcess.holderType)}
            onValueChange={(val) =>
              setFormProcess((p) => ({ ...p, holderType: fromSelectValue(val) }))
            }
          >
            <SelectTrigger id="process-holderType" className="w-full">
              <SelectValue
                placeholder={t('traceability.processManager.form.notSet')}
              />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value={NONE}>
                {t('traceability.processManager.form.notSet')}
              </SelectItem>
              {toolHolderTypes.map((opt) => (
                <SelectItem key={opt.value} value={opt.value}>
                  {t(opt.labelKey)}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>

        {/* clampingType — Select with checkbox */}
        <div className="flex flex-col gap-1.5">
          {renderLabelRow(
            'clampingType',
            t('traceability.processManager.form.prefillFields.clampingType.label'),
            'process-clampingType'
          )}
          <Select
            value={toSelectValue(formProcess.clampingType)}
            onValueChange={(val) =>
              setFormProcess((p) => ({ ...p, clampingType: fromSelectValue(val) }))
            }
          >
            <SelectTrigger id="process-clampingType" className="w-full">
              <SelectValue
                placeholder={t('traceability.processManager.form.notSet')}
              />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value={NONE}>
                {t('traceability.processManager.form.notSet')}
              </SelectItem>
              {clampingTypes.map((opt) => (
                <SelectItem key={opt.value} value={opt.value}>
                  {t(opt.labelKey)}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>

        {/* clampingBrand — SimpleInput with checkbox */}
        <div>
          {renderLabelRow(
            'clampingBrand',
            t('traceability.processManager.form.prefillFields.clampingBrand.label'),
            'process-clampingBrand'
          )}
          <SimpleInput
            name="process-clampingBrand"
            type="text"
            value={formProcess.clampingBrand ?? ''}
            onChange={(val) =>
              setFormProcess((p) => ({ ...p, clampingBrand: (val as string) || null }))
            }
            placeholder={t(
              'traceability.processManager.form.prefillFields.clampingBrand.placeholder'
            )}
          />
        </div>
      </div>
    </CollapsibleFormSection>
  );
};
